import { CheckCircle, X } from "@phosphor-icons/react";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { PROBLEM_CARDS, VALUE_CARDS, ROI_ITEMS, COMPARISON_ROWS, FAQS } from "@/lib/pricingData";

function InfoCard({ icon: Icon, title, body, light }) {
  return (
    <div
      className={
        light
          ? "bg-white border border-brand-line rounded-md p-6 transition-all hover:-translate-y-0.5 hover:shadow-md"
          : "bg-brand-cream/40 border border-brand-line rounded-md p-6"
      }
    >
      <span
        className={`inline-flex items-center justify-center w-10 h-10 rounded-md ${
          light ? "bg-brand-cream text-brand-forest" : "bg-brand-forest text-brand-bone"
        }`}
      >
        <Icon size={18} weight="duotone" />
      </span>
      <h4 className="mt-4 font-serif text-xl text-brand-forest leading-tight">{title}</h4>
      <p className="mt-3 text-sm text-brand-ink/70 leading-relaxed">{body}</p>
    </div>
  );
}

export function ProblemSection() {
  return (
    <section className="border-b border-brand-line bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <div className="max-w-3xl">
          <span className="label-eyebrow">The real bottleneck</span>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl text-brand-forest leading-tight">
            Most advisors don't have a product problem.
            <br />
            <span className="text-brand-copper">They have a lead problem.</span>
          </h2>
          <div className="mt-7 space-y-4 text-brand-ink/75 leading-relaxed">
            <p>
              IBC advisors already know the strategy works when the right business owner
              understands the numbers. The problem is getting enough serious prospects to
              that conversation.
            </p>
            <p>
              Most business owners aren't searching for "properly structured dividend-paying
              whole life." They're thinking about tax drag, idle cash, business debt,
              equipment financing, employee retention, and money leaking out of their
              company every year.
            </p>
            <p className="font-serif text-xl text-brand-forest italic">
              Build A Bank starts there.
            </p>
          </div>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {PROBLEM_CARDS.map((c) => (
            <InfoCard key={c.title} {...c} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function ValueSection() {
  return (
    <section className="border-b border-brand-line bg-brand-cream/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <div className="max-w-3xl mb-12">
          <span className="label-eyebrow">What you get</span>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl text-brand-forest leading-tight">
            A practice-growth system,
            <br />
            not just a calculator.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {VALUE_CARDS.map((c) => (
            <InfoCard key={c.title} {...c} light />
          ))}
        </div>
      </div>
    </section>
  );
}

export function RoiSection() {
  return (
    <section className="border-b border-brand-line bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <span className="label-eyebrow">Why the pricing makes sense</span>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl text-brand-forest leading-tight">
            The value isn't a software login.
          </h2>
          <p className="mt-6 text-brand-ink/75 leading-relaxed">
            Build A Bank is designed to help you create higher-quality IBC conversations
            with business owners. If one closed case produces several thousand dollars in
            first-year compensation, the platform doesn't need to create dozens of clients
            to be worth it.
          </p>
          <p className="mt-4 text-brand-ink/75 leading-relaxed">
            It only needs to help you create a small number of <strong className="text-brand-forest">better
            conversations with better prospects.</strong>
          </p>
        </div>
        <div className="md:col-span-7">
          <div className="grid sm:grid-cols-2 gap-4">
            {ROI_ITEMS.map((v, i) => (
              <div key={v} className="flex items-start gap-3 p-5 bg-brand-cream/40 border border-brand-line rounded-md">
                <span className="font-serif text-2xl text-brand-copper leading-none">{`0${i + 1}`}</span>
                <span className="text-brand-ink/85 text-sm leading-relaxed">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function ComparisonSection() {
  return (
    <section className="border-b border-brand-line bg-brand-cream/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <div className="max-w-3xl mb-10">
          <span className="label-eyebrow">Why not just a spreadsheet?</span>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl text-brand-forest leading-tight">
            Spreadsheets do math.
            <br />
            Build A Bank creates demand.
          </h2>
          <p className="mt-5 text-brand-ink/70">
            Spreadsheets are useful for internal modelling. They're not built to drive
            business-owner urgency or capture prospects on their own.
          </p>
        </div>

        <div className="bg-white border border-brand-line rounded-md overflow-hidden">
          <div className="grid grid-cols-2 bg-brand-forest text-brand-bone text-sm font-semibold uppercase tracking-wider">
            <div className="px-6 py-4">DIY Spreadsheet</div>
            <div className="px-6 py-4 bg-brand-copper">Build A Bank</div>
          </div>
          {COMPARISON_ROWS.map(([a, b], i) => (
            <div
              key={a}
              className={`grid grid-cols-2 border-t border-brand-line ${i % 2 === 0 ? "bg-white" : "bg-brand-bone/40"}`}
            >
              <div className="px-6 py-4 text-brand-ink/65 text-sm flex items-start gap-2">
                <X size={14} weight="bold" className="text-brand-muted mt-1 shrink-0" />
                <span>{a}</span>
              </div>
              <div className="px-6 py-4 text-brand-ink text-sm flex items-start gap-2 bg-brand-cream/30">
                <CheckCircle size={14} weight="fill" className="text-brand-forest mt-1 shrink-0" />
                <span className="font-medium">{b}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FaqSection() {
  return (
    <section className="border-b border-brand-line bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
        <div className="mb-10">
          <span className="label-eyebrow">FAQ</span>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl text-brand-forest leading-tight">
            Frequently asked questions.
          </h2>
        </div>

        <Accordion type="single" collapsible className="space-y-3" data-testid="pricing-faq">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={f.q}
              value={`q-${i}`}
              className="bg-white border border-brand-line rounded-md px-5 data-[state=open]:border-brand-forest/40"
            >
              <AccordionTrigger
                data-testid={`faq-trigger-${i}`}
                className="text-left font-serif text-lg text-brand-forest hover:no-underline py-5"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-brand-ink/75 leading-relaxed pb-5 text-base">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export function ComplianceSection() {
  return (
    <section className="border-b border-brand-line bg-brand-cream/40">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <span className="label-eyebrow">Important note</span>
        <h3 className="mt-3 font-serif text-2xl text-brand-forest leading-tight">
          Educational & advisor-support tool only.
        </h3>
        <div className="mt-5 space-y-4 text-sm text-brand-ink/75 leading-relaxed" data-testid="pricing-disclosure">
          <p>
            Build A Bank is an educational and advisor-support tool. It does not provide
            legal, tax, insurance, or financial advice to the public. Final recommendations,
            policy illustrations, suitability analysis, and product discussions must be
            handled by properly licensed professionals.
          </p>
          <p>
            All calculator outputs should be treated as educational estimates and conversation
            starters — not guaranteed policy values or formal financial projections.
            Participating-life-insurance dividends are declared annually by the issuing
            insurer at its sole discretion and are not guaranteed. Tax assumptions referenced
            (including the 50.17% Ontario CCPC passive-income rate and 2026 dividend-scale
            references) are subject to legislative and regulatory change.
          </p>
          <p>
            Pricing on this page is illustrative and subject to change before agreement. All
            partnerships are governed by a written Master Services Agreement (MSA) which
            supersedes anything stated here. Tax, currency (CAD), payment terms, and refund
            policy are described in the MSA.
          </p>
        </div>
      </div>
    </section>
  );
}
